import { Gesture } from "@-/com.npmjs.react-native-gesture-handler";
import {
  cancelAnimation,
  runOnJS,
  useDerivedValue,
  useSharedValue,
  withSpring,
} from "@-/com.npmjs.react-native-reanimated";
import useLatest from "@-/com.npmjs.use-latest";
import { useEffect, useMemo } from "react";
import { topSheetRevealRestHeight } from "#top-sheet-physics";

const REVEAL_SPRING = { damping: 28, mass: 0.8, overshootClamping: true, stiffness: 260 } as const;

/**
 * An anchored reveal, with free resting heights and dismissal only at the anchored edge. `edge`
 * picks which window edge the sheet grows from; a bottom sheet's anchored edge is at `anchorY`.
 */
export function useTopSheetRevealMotion({
  anchorY = 0,
  edge = "top",
  enabled,
  headerHeight,
  maximum,
  onClosed,
  onOpen,
}: {
  /** The window y of the bottom sheet's anchored (lower) edge. Unused for the top edge. */
  anchorY?: number;
  edge?: "top" | "bottom";
  enabled: boolean;
  headerHeight: number;
  maximum: number;
  onClosed: () => void;
  onOpen: () => void;
}) {
  const callbacks = useLatest({ onClosed, onOpen });
  const value = useSharedValue(0);
  const bounds = useSharedValue({ anchorY, edge, headerHeight, maximum });
  const dragStart = useSharedValue(0);
  const dragActive = useSharedValue(false);
  const progress = useDerivedValue(() => value.get() / Math.max(1, bounds.get().maximum));

  useEffect(() => {
    bounds.set({ anchorY, edge, headerHeight, maximum });
    const height = value.get();
    // Rotation/navigation/moving edges retains the chosen height, constrained by the new geometry.
    if (height > 0) {
      value.set(Math.min(maximum, Math.max(headerHeight, height)));
    }
  }, [anchorY, bounds, edge, headerHeight, maximum, value]);

  const { close, grip, handle, open, trigger } = useMemo(() => {
    const reportOpen = () => callbacks.current.onOpen();
    const reportClosed = () => callbacks.current.onClosed();
    const settleTo = (next: number) => {
      "worklet";
      value.set(
        withSpring(next, REVEAL_SPRING, (finished) => {
          "worklet";
          if (finished && next === 0) {
            runOnJS(reportClosed)();
          }
        }),
      );
    };
    const settle = (touchY: number) => {
      "worklet";
      if (!dragActive.get()) {
        return;
      }
      dragActive.set(false);
      const limits = bounds.get();
      // The finger grabs inside the 32pt handle, a little away from the sheet's free edge.
      // Reaching the anchored edge must still close it even though that grab offset leaves some
      // surface between the finger and the edge.
      settleTo(
        topSheetRevealRestHeight(
          value.get(),
          limits.headerHeight,
          limits.maximum,
          limits.edge === "top" ? touchY : limits.anchorY - touchY,
        ),
      );
    };
    // A drag on a grip resizes from wherever the sheet rests: toward the free edge grows it.
    const createGrip = () =>
      Gesture.Pan()
        .activeOffsetY([-4, 4])
        .onStart(() => {
          "worklet";
          cancelAnimation(value);
          dragActive.set(true);
          dragStart.set(value.get());
        })
        .onUpdate(({ translationY }) => {
          "worklet";
          const limits = bounds.get();
          const height = dragStart.get() + (limits.edge === "top" ? translationY : -translationY);
          value.set(Math.min(limits.maximum, Math.max(0, height)));
        })
        .onFinalize(({ absoluteY }) => {
          "worklet";
          settle(absoluteY);
        });
    const trigger = Gesture.Pan()
      .enabled(enabled)
      .activeOffsetY(12)
      .failOffsetX([-20, 20])
      .onStart(({ absoluteY }) => {
        "worklet";
        cancelAnimation(value);
        runOnJS(reportOpen)();
        const limits = bounds.get();
        if (limits.edge === "bottom") {
          // A downward swipe cannot track a sheet growing upward: it opens the sheet instead.
          settleTo(Math.max(limits.headerHeight, value.get()));
          return;
        }
        dragActive.set(true);
        value.set(Math.min(limits.maximum, Math.max(0, absoluteY)));
      })
      .onUpdate(({ absoluteY }) => {
        "worklet";
        if (dragActive.get()) {
          value.set(Math.min(bounds.get().maximum, Math.max(0, absoluteY)));
        }
      })
      .onFinalize(({ absoluteY }) => {
        "worklet";
        settle(absoluteY);
      });
    const open = () => {
      reportOpen();
      value.set(withSpring(Math.max(bounds.get().headerHeight, value.get()), REVEAL_SPRING));
    };
    const close = () => {
      cancelAnimation(value);
      settleTo(0);
    };
    // RNGH attaches a gesture to one detector at a time, so the handle and the second grip (a
    // control drawn on the sheet, such as the Admin panel's drawer button) are separate instances.
    // A drag that starts on the handle stays with the sheet in every direction, so a diagonal pull
    // never turns a pager; the grip is a control, which yields sideways drags.
    return {
      close,
      grip: createGrip().failOffsetX([-20, 20]),
      handle: createGrip().activeOffsetX([-4, 4]),
      open,
      trigger,
    };
  }, [bounds, callbacks, dragActive, dragStart, enabled, value]);

  return {
    close,
    content: handle,
    contentDraggable: false,
    grip,
    handle,
    open,
    progress,
    trigger,
    value,
  };
}
